// /app/frontend/src/components/frugal-v2/tabs/BudgetTab.jsx
// Budget configuration tab (200 lines)

import React, { useState, useEffect } from 'react';
import { Wallet, Save, Eye, EyeOff, AlertTriangle, Star } from 'lucide-react';
import { EXPENSE_CATEGORIES, VITAL_CATEGORIES, translateCategory } from '../constants/categories';
import { CATEGORY_COLORS } from '../constants/colors';
import * as api from '../services/frugalApi';

const DEFAULT_CONTINGENCY = 15;

const BudgetTab = ({ token, t, totalIncome = 0, onHasChanges, onRefresh }) => {
  const [budget, setBudget] = useState({ categories: [], contingency_percent: DEFAULT_CONTINGENCY });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hideZeros, setHideZeros] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Notificar al padre cuando hay cambios
  useEffect(() => {
    if (onHasChanges) {
      onHasChanges(hasChanges);
    }
  }, [hasChanges, onHasChanges]);

  useEffect(() => {
    const fetchBudget = async () => {
      try {
        const data = await api.fetchBudget(token);
        
        // Initialize all categories
        const allCategories = EXPENSE_CATEGORIES.map(cat => {
          const existing = data.categories?.find(c => c.category === cat);
          return {
            category: cat,
            amount: existing?.amount || 0,
            is_vital: existing?.is_vital || false
          };
        });
        
        setBudget({
          categories: allCategories,
          contingency_percent: data.contingency_percent || DEFAULT_CONTINGENCY
        });
      } catch (error) {
        console.error('Error fetching budget:', error);
        // Initialize with empty budget
        setBudget({
          categories: EXPENSE_CATEGORIES.map(cat => ({ category: cat, amount: 0, is_vital: false })),
          contingency_percent: DEFAULT_CONTINGENCY
        });
      } finally {
        setLoading(false);
      }
    };
    
    if (token) fetchBudget();
  }, [token]);

  const handleAmountChange = (category, value) => {
    const amount = parseFloat(value) || 0;
    setBudget(prev => ({
      ...prev,
      categories: prev.categories.map(c => 
        c.category === category ? { ...c, amount } : c
      )
    }));
    setHasChanges(true);
  };

  const handleVitalToggle = (category) => {
    // Defaults are inmutable: cannot be unmarked.
    if (VITAL_CATEGORIES.includes(category)) return;
    setBudget(prev => ({
      ...prev,
      categories: prev.categories.map(c =>
        c.category === category ? { ...c, is_vital: !c.is_vital } : c
      )
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send categories with amounts > 0 OR flagged as vital (preserves preference)
      const categoriesToSave = budget.categories.filter(c => c.amount > 0 || c.is_vital);
      await api.saveBudget(token, {
        categories: categoriesToSave,
        contingency_percent: budget.contingency_percent
      });
      setHasChanges(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error saving budget:', error);
    } finally {
      setSaving(false);
    }
  };

  const totalBudget = budget.categories.reduce((sum, c) => sum + (c.amount || 0), 0);
  // vital_budget = defaults + categories with is_vital=true (matches backend logic)
  const vitalBudget = budget.categories
    .filter(c => VITAL_CATEGORIES.includes(c.category) || c.is_vital)
    .reduce((sum, c) => sum + (c.amount || 0), 0);

  // Available for Contingency Reserve and Savings = Income − Budget
  const availableForReserves = totalIncome - totalBudget;

  // Verificar si el presupuesto excede los ingresos
  const budgetExceedsIncome = totalIncome > 0 && totalBudget > totalIncome;
  const exceedAmount = totalBudget - totalIncome;

  // Sort categories alphabetically and apply filter
  const sortedCategories = [...budget.categories].sort((a, b) => 
    a.category.localeCompare(b.category)
  );
  const displayCategories = hideZeros 
    ? sortedCategories.filter(c => c.amount > 0)
    : sortedCategories;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-pulse text-emerald-400">{t('frugal.status.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Wallet className="w-5 h-5 text-emerald-400" />
          <h2 className="text-lg font-semibold text-white">{t('frugal.budget.title')}</h2>
        </div>
        <button
          onClick={() => setHideZeros(!hideZeros)}
          className="flex items-center gap-1 text-sm text-slate-400 hover:text-white"
        >
          {hideZeros ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          {hideZeros ? t('frugal.budget.showAll') : t('frugal.budget.hideZeros')}
        </button>
      </div>

      {/* Budget Overview Card: Income / Budget / Available */}
      <div className="bg-gradient-to-br from-emerald-500/10 to-sky-500/10 border border-emerald-500/20 rounded-xl p-4 space-y-3" data-testid="budget-overview-card">
        <div className="flex items-center justify-between">
          <span className="text-slate-300 text-sm">{t('frugal.budget.totalIncome')}</span>
          <span className="text-emerald-400 font-semibold">
            ${totalIncome.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-slate-300 text-sm">{t('frugal.budget.totalBudget')}</span>
          <span className="text-amber-300 font-semibold">
            ${totalBudget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        <div className="border-t border-white/10 pt-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white font-medium">
                {t('frugal.budget.availableForReserves')}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {t('frugal.budget.availableHint')}
              </div>
            </div>
            <span
              className={`text-lg font-bold whitespace-nowrap ${availableForReserves < 0 ? 'text-rose-400' : 'text-sky-300'}`}
              data-testid="available-for-reserves"
            >
              ${availableForReserves.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>

      {/* Alerta: Presupuesto excede ingresos */}
      {budgetExceedsIncome && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-400 font-medium">
              {t('frugal.budget.exceedsIncome', '¡Presupuesto excede tus ingresos!')}
            </p>
            <p className="text-sm text-slate-400">
              {t('frugal.budget.exceedsAmount', { amount: exceedAmount.toFixed(2) }, 
                `Tu presupuesto es $${exceedAmount.toFixed(2)} mayor que tus ingresos de $${totalIncome.toFixed(2)}`)}
            </p>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white/5 rounded-xl p-3 text-center">
          <div className="text-xs text-slate-400 mb-1">{t('frugal.budget.totalBudget')}</div>
          <div className="text-lg font-bold text-white">
            ${totalBudget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
        <div className="bg-white/5 rounded-xl p-3 text-center">
          <div className="text-xs text-slate-400 mb-1">{t('frugal.budget.vitalBudget')}</div>
          <div className="text-lg font-bold text-sky-400">
            ${vitalBudget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
      </div>

      {/* Categories List */}
      <div className="space-y-2">
        {displayCategories.map(cat => {
          const isDefaultVital = VITAL_CATEGORIES.includes(cat.category);
          const isVital = isDefaultVital || cat.is_vital;
          const color = CATEGORY_COLORS[cat.category] || CATEGORY_COLORS.Otros;
          
          return (
            <div
              key={cat.category}
              className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
              data-testid={`budget-row-${cat.category}`}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: color }}
                />
                <span className="text-sm text-white truncate">
                  {translateCategory(t, cat.category)}
                  {isVital && (
                    <span className="text-sky-400 text-xs ml-1">
                      ({t('frugal.budget.vitalReserve')})
                    </span>
                  )}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Vital toggle (defaults are inmutable) */}
                <button
                  type="button"
                  onClick={() => handleVitalToggle(cat.category)}
                  disabled={isDefaultVital}
                  title={
                    isDefaultVital
                      ? t('frugal.budget.vitalLocked')
                      : isVital
                        ? t('frugal.budget.unmarkVital')
                        : t('frugal.budget.markVital')
                  }
                  className={`p-1.5 rounded-md transition-colors ${
                    isVital
                      ? 'text-sky-400 bg-sky-500/15 hover:bg-sky-500/25'
                      : 'text-slate-500 hover:text-sky-300 hover:bg-white/10'
                  } ${isDefaultVital ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'}`}
                  data-testid={`budget-vital-toggle-${cat.category}`}
                >
                  <Star className={`w-4 h-4 ${isVital ? 'fill-current' : ''}`} />
                </button>
                <span className="text-slate-400">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={cat.amount || ''}
                  onChange={(e) => handleAmountChange(cat.category, e.target.value)}
                  placeholder="0.00"
                  className="w-24 px-2 py-1 bg-white/10 border border-white/20 text-white text-right text-sm rounded-md focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Save Button */}
      {hasChanges && (
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
        >
          <Save className="w-5 h-5" />
          {saving ? t('frugal.status.loading') : t('frugal.actions.save')}
        </button>
      )}
    </div>
  );
};

export default BudgetTab;
